import { EmbedContext, EmbedContextOptions } from '@thoughtspot/blink-context';
import { create } from '@thoughtspot/logger';
import { UserAction, userActionTypeEnum } from '@thoughtspot/metrics';
import { setCustomLinkOverride } from '@thoughtspot/radiant-react/widgets/link';
import cx from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { Subject } from 'rxjs';
import { useAppContext } from '/@contexts/global-app-context';
import { setEmbedInitData } from '/@services/everywhere/everywhere-service.util';
import {
    EventType,
    getCustomActions,
    getEmbedQueryParamsFromInitData,
    getHiddenHomeLeftNavItems,
    getHiddenHomepageModules,
    getReorderedHomepageModules,
    getRuntimeFilterParams,
    getRuntimeParameterParams,
    isDisableRedirectionLinksInNewTabEnabled,
    isLinkOverridesV2Enabled,
    isSpotterAgentEmbed,
    useEmbedEventsRootHandler,
    useLayoutChangeNotifier,
} from '/@utils/embed.util';
import {
    addCustomCssElements,
    EmbedParams,
    getEmbedQueryParams,
    IncomingRuntimeParameter,
    isLazyLoadingForEmbed,
    parseQueryParams,
    updateParametersWithOverrides,
    updateRuntimeFiltersWithOverrides,
} from '/@utils/embed-base.util';
import { nonMemoizedEventTypes } from '/@utils/event-bridge/core/constants';
import { getHostEventConfig } from '/@utils/event-bridge/core/utils';
import { dispatchValidationError } from '/@utils/event-bridge/core/validation';
import {
    createEventSubscriber,
    destroy,
    dispatchEvent,
    init,
} from '/@utils/event-bridge/event-bridge';
import { Footer } from '../footer/footer';
import { useCodeBasedCustomActionsFilter } from './custom-actions';
import styles from './embed.module.scss';
import { EmbedCustomLinkOverride } from './embed-custom-link-override';
import { VisibleViewportEmbedContainer } from './visibleViewportEmbed.container';

const logger = create('embed-container');

export const parseRuntimeParams = (queryString?: string): EmbedParams => {
    const parsed = {};
    if (queryString) {
        parseQueryParams(queryString, parsed);
    }
    return parsed;
};

export const EmbedContainer: React.FC<{
    component: React.ReactElement;
    doNotWrapInContainer?: boolean;
}> = ({ component, doNotWrapInContainer = false }) => {
    const embedParamsFromInitData = getEmbedQueryParamsFromInitData();
    // Give more priority to the embed params from init data
    const queryParams = {
        ...getEmbedQueryParams(),
        ...embedParamsFromInitData,
    };
    const runtimeFilter = getRuntimeFilterParams();
    const runtimeParameter = getRuntimeParameterParams();

    // Maintain embed params in a state, so that it can be updated based on any of the embed events.
    const [embedParams, updateEmbedParams] = useState<EmbedParams>(queryParams);

    const hiddenHomeLeftNavItems =
        embedParams.hiddenHomeLeftNavItems || getHiddenHomeLeftNavItems();
    const hiddenHomepageModules =
        embedParams.hiddenHomepageModules || getHiddenHomepageModules();
    const reorderedHomepageModules =
        embedParams.reorderedHomepageModules || getReorderedHomepageModules();
    const codeBasedCustomActions =
        embedParams.customActions ?? getCustomActions();

    const [runtimeFilterParams, updateRuntimeFilterParams] = useState<
        EmbedParams
    >(runtimeFilter);
    const [embedParameterParams, updateEmbedParameterParams] = useState<
        EmbedParams
    >(runtimeParameter);
    const setParameterOverrideSubjectRef = useRef(
        new Subject<IncomingRuntimeParameter[]>(),
    );
    const {
        sessionService,
        customStylingService,
        metricsService,
    } = useAppContext();
    const useEmbedEventSubscription = createEventSubscriber();
    const dispatchEmbedEvent = dispatchEvent(nonMemoizedEventTypes);

    const isPoweredFooterHidden =
        sessionService.isPoweredFooterHiddenForFullEmbed() ||
        sessionService.isPoweredFooterHiddenForVizEmbed() ||
        isSpotterAgentEmbed();
    const isLiveboardV2Enabled = sessionService.isPinboardV2Enabled();

    // On changes in layout, send the updated height to parent frame.
    const ref = useRef<HTMLDivElement>(null);
    const { updateHeight } = useLayoutChangeNotifier(dispatchEmbedEvent);
    const resizeTimeout = useRef(null);

    // Use the custom hook for filtering code-based custom actions
    const { filteredCodeBasedCustomActions } = useCodeBasedCustomActionsFilter({
        codeBasedCustomActions,
        dispatchEmbedEvent,
    });

    const onWindowResize = () => {
        if (ref.current && isLiveboardV2Enabled) {
            ref.current.style.maxHeight = '0px';
            const embedHeight = ref.current.children[0]?.scrollHeight;
            ref.current.style.maxHeight = '';
            updateHeight(embedHeight);
        }
    };

    const debounceWindowResize = () => {
        clearTimeout(resizeTimeout.current);
        resizeTimeout.current = setTimeout(() => {
            onWindowResize();
        }, 700);
    };

    useEffect(() => {
        window.addEventListener('resize', debounceWindowResize);
        return () => window.removeEventListener('resize', debounceWindowResize);
    });

    const embedConfig: EmbedContextOptions = {
        isEmbedded: true,
        isPoweredFooterHidden,
        useEmbedEventSubscription,
        dispatchEmbedEvent,
        embedParams,
        updateEmbedParams,
        runtimeFilterParams,
        updateRuntimeFilterParams,
        embedParameterParams,
        updateEmbedParameterParams,
        setParameterOverridesSubject: setParameterOverrideSubjectRef.current,
        hiddenHomeLeftNavItems,
        onLayoutChange: () => {
            if (ref.current) {
                ref.current.style.maxHeight = '0px';
                const embedHeight = ref.current.children[0]?.scrollHeight;
                ref.current.removeAttribute('style');
                updateHeight(embedHeight);
            }
        },
        hiddenHomepageModules,
        reorderedHomepageModules,
        codeBasedCustomActions: filteredCodeBasedCustomActions,
        dispatchValidationError,
    };

    useEmbedEventSubscription(
        EventType.UpdateEmbedParams,
        (embedConfigFromEvent: EmbedParams) => {
            // Convert array format to query params format
            const filters = embedConfigFromEvent.runtimeFilters
                ? updateRuntimeFiltersWithOverrides(
                      {},
                      embedConfigFromEvent.runtimeFilters,
                  )
                : {};
            const parameters = embedConfigFromEvent.runtimeParameters
                ? updateParametersWithOverrides(
                      {},
                      embedConfigFromEvent.runtimeParameters,
                  )
                : {};

            const processedParams = {
                ...embedConfigFromEvent,
                ...filters,
                ...parameters,
            };

            delete processedParams.runtimeFilters;
            delete processedParams.runtimeParameters;

            updateEmbedParams(processedParams);

            // Written unconditionally: the event carries the full param set, so an absent
            // value means "none" and skipping the write leaked state across a pre-render.
            updateEmbedParameterParams(
                parseRuntimeParams(embedConfigFromEvent.runtimeParameterParams),
            );
            updateRuntimeFilterParams(
                parseRuntimeParams(embedConfigFromEvent.runtimeFilterParams),
            );

            addCustomCssElements(embedConfigFromEvent);
            setEmbedInitData(embedConfigFromEvent);
        },
    );

    const hostevent2Config = getHostEventConfig();

    // Handle the embed events at the root level.
    useEmbedEventsRootHandler(embedConfig, hostevent2Config);

    useEffect(() => {
        const isLinkOverridesV2Active = isLinkOverridesV2Enabled();
        const isRedirectionDisabled = isDisableRedirectionLinksInNewTabEnabled();

        // Register the custom link override if either flag is enabled
        if (isLinkOverridesV2Active || isRedirectionDisabled) {
            setCustomLinkOverride(EmbedCustomLinkOverride);
            metricsService.collectEvent(
                new UserAction(userActionTypeEnum.EMBED_LINK_OVERRIDE_CONFIG),
                {
                    linkOverridesV2Enabled: isLinkOverridesV2Active,
                    disableRedirectionEnabled: isRedirectionDisabled,
                    embedType: embedParams.embedType,
                    isFullAppEmbed: embedParams.isFullApp || false,
                },
            );
        }

        init();
        return () => destroy();
    }, []);

    const isFullHeight = embedParams?.isFullHeightPinboard;

    // Resize observer is enabled only if full height is enabled and resize observer is not disabled.
    const isResizeObserverEnabled =
        isFullHeight && !embedParams?.disableFullHeightResizeObserver;

    const resizeDivRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isResizeObserverEnabled) {
            const resizeObserver = new ResizeObserver(() => {
                embedConfig.onLayoutChange();
                logger.info('Embed Container: ResizeObserver triggered.');
            });
            if (resizeDivRef.current) {
                resizeObserver.observe(resizeDivRef.current);
            }

            return () => {
                resizeObserver?.disconnect();
            };
        }

        return () => null;
    }, [embedParams]);

    const footerText = customStylingService.getConfig()?.getFooterText() || '';
    const childParams = { ...queryParams };
    const wrapperClassName = cx(
        styles.tsEmbedWrapper,
        isFullHeight ? styles.fullheight : null,
    );

    const containerClassName = cx(
        styles.tsEmbedContainer,
        isFullHeight ? styles.fullheight : null,
    );

    const child = React.cloneElement(component, childParams);

    // This should be the direct parent of the child to get height resize properly
    const childWithResizeObserverWrapper = isResizeObserverEnabled ? (
        <div
            ref={resizeDivRef}
            data-testid="embed-resize-component"
            className={styles.fullheightResizeObserverWrapper}
        >
            {child}
        </div>
    ) : (
        child
    );

    const childWithContainerWrapper = doNotWrapInContainer ? (
        <div
            ref={ref}
            data-testid="test-component"
            className={wrapperClassName}
            data-embed-wrapper="true"
        >
            {childWithResizeObserverWrapper}
        </div>
    ) : (
        <div
            ref={ref}
            data-testid="test-component"
            className={containerClassName}
        >
            {/* Pass in query params as props to the embedded component */}
            {childWithResizeObserverWrapper}
            {!isPoweredFooterHidden && (
                <div className={styles.footerWrapper}>
                    <Footer footerText={footerText} />
                </div>
            )}
        </div>
    );

    const isLazyLoadingForEmbedEnabled = isLazyLoadingForEmbed(embedConfig);

    // This should be the top most parent.
    const childWithALazyLoadingWrapper = isLazyLoadingForEmbedEnabled ? (
        <VisibleViewportEmbedContainer>
            {childWithContainerWrapper}
        </VisibleViewportEmbedContainer>
    ) : (
        childWithContainerWrapper
    );

    return (
        <EmbedContext.Provider value={embedConfig}>
            {childWithALazyLoadingWrapper}
        </EmbedContext.Provider>
    );
};

export default EmbedContainer;
